import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import moment from 'moment'

import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import config from 'constants/config'
import shouldHideAoLabel from 'lib/hideAoLabel'
import { ImageParams } from 'components/Common/ResponsiveImage'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import { connect } from 'react-redux'
import { take } from 'lib/arrayUtils'
import { getCustomerType } from 'lib/peopleLikeMeUtils'
import useHotelOfferSales from 'hooks/useHotelOfferSales'
import PeopleLikeMeSalesBanner from '../../../../SearchV2/Components/PeopleLikeMe/PeopleLikeMeSalesBanner'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import ViewBundleOffer from '.././ViewBundleOffer'
import SearchOfferTileInclusionsUpsellBanner from '../SearchOfferTileInclusionsUpsellBanner'
import SearchOfferTileInclusionsLPCUpsellBanner from '../SearchOfferTileInclusionsLPCUpsellBanner'
import useSearchTileInclusionUpsellBestPrice from 'hooks/useSearchTileInclusionUpsellBestPrice'
import SearchTilePropertyDetails from './SearchTilePropertyDetails'
import SearchTilePriceDetails from './SearchTilePriceDetails'
import {
  Main,
  MainImageSegment,
  OfferOverlayContainer,
  StyledOfferBadge,
  DetailSegment,
  StyledCSSBreakpoint,
} from './SearchTileStyles'

const UpsellWrapper = styled.div`
  grid-column: 1 / 3;
  margin-top: ${rem(8)};
  align-self: flex-end;
`

const imageParams: ImageParams = {
  quality: 'good',
  mdWidth: '768px',
}

interface MappedStateProps {
  iOS: boolean;
  shouldShowSaleNumbers: boolean;
  windowSearch: string;
}

interface Props {
  offer: App.Offer | App.OfferSummary;
  passedWalledGarden: boolean;
  productClick?: (offer: App.Offer | App.OfferSummary) => void;
  offerMetaData?: App.OfferListMetaData;
  filters?: App.OfferListFilters;
  bestPrice: App.OfferAvailableRate;
  soldOut: boolean;
  bestPricePackage: App.Package;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
  offerLinkIncludesFilters?: boolean;
}

function NewSearchOfferTileExpanded(props: Props & MappedStateProps) {
  const {
    offer,
    passedWalledGarden,
    filters,
    bestPrice,
    bestPricePackage,
    eagerLoadFirstImage,
    offerUrl,
    iOS,
    shouldShowSaleNumbers,
    windowSearch,
    offerMetaData,
    offerLinkIncludesFilters,
    soldOut,
  } = props

  const { type, isPartnerProperty } = offer

  const customerType = useMemo(
    () => getCustomerType(windowSearch),
    [windowSearch],
  )
  const [count, loading] = useHotelOfferSales(offer?.id, customerType)

  const hideAoLabel = shouldHideAoLabel(offer)

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  // This needs a memo because new moment objects aren't stable refs.
  const checkInDate = useMemo(() => {
    return filters?.checkIn ? moment(filters.checkIn) : undefined
  }, [filters?.checkIn])

  const { shouldShowUpsell: showInclusionUpsell, meetTactical } =
    useSearchTileInclusionUpsellBestPrice(offer, filters)

  const location = [offer.locationHeading, offer.locationSubheading]
    .filter((t) => t)
    .join(', ')
  const cancellationPolicyType =
    bestPricePackage.roomRate?.cancellationPolicy?.type

  // This is temporary until we can improve memory on the offer listing page
  const images = iOS ? take(offer.images, 1, 0) : offer.images

  return (
    <Main className={cn({ 'is-partner-property': isPartnerProperty })}>
      <MainImageSegment>
        <ImageCarousel
          images={images}
          imageParams={imageParams}
          eagerLoadFirstImage={eagerLoadFirstImage}
        />
        {type === OFFER_TYPE_ALWAYS_ON && (
          <OfferOverlayContainer
            className={cn({ 'hide-type-label': hideAoLabel })}
          >
            <OfferPartnershipLabel offer={offer} variant="icon" />
          </OfferOverlayContainer>
        )}
        {showBadge && offer.badge && (
          <StyledOfferBadge
            badge={offer.badge}
            dimensions={{ width: 86, height: 86 }}
          />
        )}
      </MainImageSegment>
      <div>
        {shouldShowSaleNumbers && !loading && (
          <PeopleLikeMeSalesBanner
            customerType={customerType}
            saleCount={count}
          />
        )}
        <DetailSegment>
          <TripGuard>
            <StyledCSSBreakpoint min="sm">
              <FloatingBookmarkContainer>
                <BookmarkButton offer={offer} />
              </FloatingBookmarkContainer>
            </StyledCSSBreakpoint>
            <StyledCSSBreakpoint max="xs">
              <OffsetBookmarkContainer>
                <BookmarkButton offer={offer} />
              </OffsetBookmarkContainer>
            </StyledCSSBreakpoint>
          </TripGuard>
          <SearchTilePropertyDetails
            location={location}
            cancellationPolicyType={cancellationPolicyType}
            offer={offer}
            checkInDate={checkInDate}
            filters={filters}
            offerUrl={offerUrl}
          />
          <SearchTilePriceDetails
            offer={offer}
            soldOut={soldOut}
            offerUrl={offerUrl}
            bestPriceForDates={bestPrice}
            filters={filters}
            checkInDate={checkInDate}
            bestPricePackage={bestPricePackage}
            passedWalledGarden={passedWalledGarden}
          />
          {config.ENABLE_BUNDLE_AND_SAVE && offerMetaData?.bundleOfferId && (
            <UpsellWrapper>
              <ViewBundleOffer
                offer={offer}
                filters={filters}
                offerMetaData={offerMetaData}
                offerLinkIncludesFilters={offerLinkIncludesFilters}
              />
            </UpsellWrapper>
          )}
          {!offerMetaData?.bundleOfferId && showInclusionUpsell && !meetTactical && (
            <UpsellWrapper>
              <SearchOfferTileInclusionsUpsellBanner
                offer={offer}
                filters={filters}
                newTile
              />
            </UpsellWrapper>
          )}
          {!offerMetaData?.bundleOfferId && showInclusionUpsell && meetTactical && (
            <UpsellWrapper>
              <SearchOfferTileInclusionsLPCUpsellBanner
                offer={offer}
                filters={filters}
                newTile
              />
            </UpsellWrapper>
          )}
        </DetailSegment>
      </div>
    </Main>
  )
}

NewSearchOfferTileExpanded.defaultProps = {
  filters: {},
}

const mapStateToProps = (state: App.State): MappedStateProps => ({
  iOS: state.config.userAgent.osName === 'iOS',
  shouldShowSaleNumbers:
    state.router.location.pathname.endsWith('people-like-me'),
  windowSearch: state.router.location.search,
})

export default connect(mapStateToProps)(NewSearchOfferTileExpanded)
